import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {EmailState} from 'src/reducers/email';
import type {NodeSchedule} from 'src/types/node-schedule';
import type {NoteWriteback} from 'src/types/note-writeback';

import type {StandardEntity} from 'src/types/target-entity-types';
import type {EntityType} from 'src/types/ats-entities';
import * as reduxApi from 'src/utils/redux-api-bff';

import {
  parseEmail,
  normalizeEmail,
  getIncomingRelatedEntityGroupIds,
} from 'src/utils/email';
import {key, fetching} from 'src/utils/redux';
import {thunkify as flow} from 'src/utils/thunks';


export const RECEIVE_EMAIL_INFO = 'email/receive';
export const UPDATE_EMAIL_INFO = 'email/update';

export const UPDATE_NODE_SCHEDULE = 'email/update-node-schedule';
export const UPDATE_NOTE_WRITEBACK = 'email/updateNoteWriteback';
export const RESET = 'email/reset';

type UpdateEmailInfoPayload = Partial<EmailState>;

type ReceiveEmailInfoAction = {
  type: 'email/receive';
  payload: EmailState;
};

type UpdateEmailInfoAction = {
  type: 'email/update';
  payload: UpdateEmailInfoPayload;
};

type ResetStateAction = {
  type: 'email/reset';
};

type UpdateNodeScheduleAction = {
  type: 'email/update-node-schedule';
  payload: Partial<NodeSchedule>;
};

type UpdateNoteWritebackAction = {
  type: 'email/updateNoteWriteback';
  payload: Partial<NoteWriteback>;
};

export type EmailAction =
  | ReceiveEmailInfoAction
  | UpdateEmailInfoAction
  | ResetStateAction
  | UpdateNodeScheduleAction
  | UpdateNoteWritebackAction;

export const reset = (): ResetStateAction => ({type: RESET});

export const receiveEmailNode = (
  payload: EmailState,
): ReceiveEmailInfoAction => ({
  type: RECEIVE_EMAIL_INFO,
  payload,
});

export const getEmailTaskNode: (node_id: string) => ThunkAction<unknown> = flow(
  key((id) => `getNodeInfo-${id}`),
  fetching(),
)((node_id) => async (dispatch: Dispatch) => {
  const emailNode = await dispatch(
    reduxApi.get(`node/composite`, {
      node_id,
    }),
  );
  console.log('emailNode', emailNode);

  const parsedEmailNode = parseEmail(emailNode.body);
  return dispatch(receiveEmailNode(parsedEmailNode));
});

export const updateEmailInfo = (
  payload: UpdateEmailInfoPayload,
): UpdateEmailInfoAction => ({
  type: UPDATE_EMAIL_INFO,
  payload,
});

export const uploadAttachment: (
  nodeId: string,
  file: File,
) => ThunkAction<void> = flow(
  key((_, file) => file.name),
  fetching(),
)((nodeId: string, file: File) => async (dispatch: Dispatch) => {
  return await dispatch(
    reduxApi.put(
      `node/attachments?node_id=${nodeId}`,
      {file},
      {},
      {
        multipart: true,
      },
    ),
  );
});

export const deleteAttachment: (
  nodeId: string,
  id: string,
) => ThunkAction<unknown> = (nodeId, id) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.del(`node/attachments/${id}`));
};

export const getPreviewBeefreeHtml: (arg1: {
  entity_id: string | null | undefined;
  workflowId: string;
  swimlaneId: string;
  workflowEntityType: EntityType;
  nodeId: string;
  secondaryEntityId: string | null;
  secondaryEntityType: string | null;
}) => ThunkAction<unknown> =
  ({
    entity_id,
    workflowId,
    swimlaneId,
    workflowEntityType,
    nodeId,
    secondaryEntityId,
    secondaryEntityType,
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (entity_id == null) {
      return dispatch(getPreviewBeefreeHtmlWithoutReplacements());
    }
    const email = getState().email;
    const composite_node = normalizeEmail(email);

    const apiPayload = {
      composite_node: {
        composite_id: nodeId,
        composite_node_type: 'email',
        body: composite_node,
      },
      execution: {
        entity_id,
        entity_type: workflowEntityType,
        secondary_entity_id: secondaryEntityId,
        secondary_entity_type: secondaryEntityType,
      },
    } as const;

    return dispatch(reduxApi.post(`node/preview`, apiPayload));
  };

export const getPreviewBeefreeHtmlWithoutReplacements: () => ThunkAction<unknown> =
  () => (dispatch: Dispatch, getState: GetState) => {
    const email = getState().email;
    const beefree_json = email.content_json;

    return dispatch(reduxApi.post(`email/beefree-json-html`, {beefree_json}));
  };

// export const getEntityGroupAttributes: (
//   entityId: string,
//   entityType?: EntityType,
// ) => ThunkAction<mixed> =
//   (entityId, entityType) => async (dispatch: Dispatch) => {
//     const body = {
//       entity_type: entityType,
//       entity_id: entityId,
//       is_truncated: false,
//       level: 3,
//     };
//     const res = await dispatch(reduxApi.post('entity_group', body));
//     return getAttributeDataFromEntityGroup(res);
//   };

export const getIncomingPersonRelatedEntities: (
  entityId: string,
  entityType: EntityType,
  workflowStandardEntity: StandardEntity,
) => ThunkAction<unknown> =
  (entityId, entityType, workflowStandardEntity) =>
  async (dispatch: Dispatch) => {
    const body = {
      entity_id: entityId,
      entity_type: entityType,
    } as const;
    const res = await dispatch(reduxApi.post('entity_group/profile', body));
    return res.incoming_related_entities.length > 0
      ? getIncomingRelatedEntityGroupIds(
          res.incoming_related_entities,
          workflowStandardEntity,
        )
      : [];
  };

export const getHiddenBranchTemplate: (
  taskType: string,
) => ThunkAction<unknown> = (taskType) => async (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`email/hidden-branch-template/${taskType}`));
};

export const getJourneyEmailContent: (
  workflowId: string,
  touchpointId: string,
) => ThunkAction<unknown> =
  (workflowId, touchpointId) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get(
        `email/journeys/${workflowId}/events/${touchpointId}/copy-legacy-email`,
      ),
    );
  };
export const getAutomationWorkflowEmailContent: (
  workflowId: string,
  nodeId: string,
) => ThunkAction<unknown> = (workflowId, nodeId) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`node/${nodeId}/content-json`));
};

export const updateNodeSchedule = (
  payload: Partial<NodeSchedule>,
): UpdateNodeScheduleAction => ({
  type: UPDATE_NODE_SCHEDULE,
  payload,
});

export const updateNoteWriteback = (
  payload: Partial<NoteWriteback>,
): UpdateNoteWritebackAction => ({
  type: UPDATE_NOTE_WRITEBACK,
  payload,
});
